import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import Me from '../img/Torky.png'
import { motion, AnimatePresence } from 'framer-motion'
import pdf from '../data/resume.pdf'
import projectsData from '../data/projects.json'
import servicesData from '../data/services.json'
import { ProjectDetailModal } from './projects'
import { SHOW_PRICES } from '../config/priceConfig'

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay } }
})

const TECH = ['React', 'Node.js', 'Python', 'MongoDB', 'Next.js', 'AWS', 'TailwindCSS', 'Firebase', 'PowerBI', 'Git']

const FloatingOrb = ({ style }) => (
  <motion.div
    animate={{ y: [0, -20, 0], scale: [1, 1.05, 1] }}
    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: style.delay || 0 }}
    className="absolute rounded-full pointer-events-none"
    style={style}
  />
)

// 1. EXTRACTED HOME PROJECT CARD COMPONENT (STABLE RATIO & CLICK MODAL SUPPORT)
const HomeProjectCard = ({ p, i, language, darkMode, onClick }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const scrollRef = useRef(null)

  const isExternalDemo = p.liveDemo && p.liveDemo.startsWith('http')
  const isGraphicDesign = p.technology === "Graphic Design" || p.id === 9

  useEffect(() => {
    let interval
    if (isHovered && scrollRef.current && !isExternalDemo && !isGraphicDesign) {
      const container = scrollRef.current
      const scrollSpeed = 0.6 // slow premium scroll speed
      interval = setInterval(() => {
        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 1) {
          clearInterval(interval)
        } else {
          container.scrollTop += scrollSpeed
        }
      }, 16)
    } else if (!isHovered && scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
    return () => clearInterval(interval)
  }, [isHovered, isExternalDemo, isGraphicDesign])

  return (
    <motion.div
      key={p.id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.1, duration: 0.5 }}
      whileHover={{ y: -8 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIframeLoaded(false)
      }}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => {
        setTimeout(() => setIsHovered(false), 2000)
      }}
      onClick={onClick}
      className="group relative rounded-3xl overflow-hidden cursor-pointer flex flex-col h-full bg-white/5 border transition-shadow duration-300"
      style={{
        background: darkMode ? '#0d0d1a' : '#ffffff',
        border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
        boxShadow: darkMode ? 'none' : '0 10px 30px rgba(0,0,0,0.02)'
      }}
    >
      {/* Stable aspect ratio container to prevent layout shifting flickering */}
      <div className="relative w-full aspect-[16/10] overflow-hidden select-none bg-[#07070f] border-b border-black/5 dark:border-white/5">
        {isGraphicDesign ? (
          /* UNIQUE GRAPHIC COLLAGE PREVIEW */
          <div className="relative w-full h-full flex items-center justify-center p-3">
            <motion.div
              animate={{
                rotate: isHovered ? -12 : -4,
                x: isHovered ? -35 : -6,
                scale: isHovered ? 0.85 : 0.9
              }}
              transition={{ type: 'spring', stiffness: 220, damping: 18 }}
              className="absolute w-[50%] h-[75%] rounded-xl border border-white/10 bg-slate-800 shadow-xl overflow-hidden pointer-events-none opacity-50 z-0"
            >
              <img src={p.image} alt="art-back-left" className="w-full h-full object-cover filter brightness-50 blur-[0.5px]" />
            </motion.div>
            <motion.div
              animate={{
                rotate: isHovered ? 12 : 4,
                x: isHovered ? 35 : 6,
                scale: isHovered ? 0.85 : 0.9
              }}
              transition={{ type: 'spring', stiffness: 220, damping: 18 }}
              className="absolute w-[50%] h-[75%] rounded-xl border border-white/10 bg-slate-800 shadow-xl overflow-hidden pointer-events-none opacity-50 z-0"
            >
              <img src={p.image} alt="art-back-right" className="w-full h-full object-cover filter brightness-50 blur-[0.5px]" />
            </motion.div>
            <motion.div
              animate={{
                scale: isHovered ? 1.05 : 0.95,
                y: isHovered ? -4 : 0
              }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              className="relative w-[55%] h-[80%] rounded-xl border border-white/20 shadow-2xl overflow-hidden bg-slate-900 z-10"
            >
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            </motion.div>
          </div>
        ) : isHovered && isExternalDemo ? (
          /* BROWSER INTERACTIVE WEB PREVIEW */
          <div className="w-full h-full flex flex-col bg-[#0b0b1a]">
            {/* Header bar */}
            <div className="w-full bg-black/50 border-b border-white/5 px-3 py-1 flex items-center gap-1.5">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff5f56]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#ffbd2e]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#27c93f]" />
              </div>
              <div className="bg-white/5 border border-white/10 rounded-md text-[8px] text-gray-400 px-2 py-0.5 flex-grow mx-3 text-center truncate font-mono">
                {p.liveDemo.replace('https://', '').replace('/', '')}
              </div>
            </div>

            <div className="relative flex-grow w-full bg-white h-[calc(100%-20px)]">
              {!iframeLoaded && (
                <div className="absolute inset-0 bg-[#0b0b1a] flex items-center justify-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="w-4 h-4 rounded-full border border-purple-500 border-t-transparent animate-spin" />
                    <span className="text-[7px] text-gray-400 uppercase tracking-widest font-black">
                      {language === 'ar' ? 'تحميل...' : 'Loading...'}
                    </span>
                  </div>
                </div>
              )}
              <iframe
                src={p.liveDemo}
                title={p.name}
                onLoad={() => setIframeLoaded(true)}
                className="w-full h-full border-none pointer-events-none select-none"
              />
            </div>
          </div>
        ) : (
          /* STATIC COVER */
          <div ref={scrollRef} className="w-full h-full overflow-y-auto overflow-x-hidden custom-scrollbar">
            <img
              src={p.image}
              alt={p.name}
              className={`w-full ${isHovered ? 'h-auto object-top' : 'h-full object-cover object-top'} transition-opacity duration-300`}
            />
            {!isHovered && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-100 transition-opacity duration-300" />
            )}
            <AnimatePresence>
              {isHovered && !isExternalDemo && !isGraphicDesign && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute bottom-3 right-3 z-10 px-2.5 py-1 rounded-full bg-purple-600/90 text-white text-[9px] font-black uppercase tracking-wider flex items-center gap-1 shadow-lg pointer-events-none"
                >
                  <span className="w-1 h-1 rounded-full bg-white animate-ping" />
                  {language === 'ar' ? 'مرر للتصفح' : 'Scroll down'}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Details info */}
      <div className="p-5 flex flex-col flex-grow bg-transparent transition-colors duration-300 text-start">
        <div className="flex items-center justify-between mb-2">
          <span
            className="text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full"
            style={{ background: 'rgba(168,85,247,0.15)', color: '#a855f7' }}
          >
            {p.technology}
          </span>
        </div>
        <h3 className="text-base font-black text-darktheme dark:text-white mb-2 leading-tight group-hover:text-purple-500 transition-colors">
          {language === 'ar' ? p.arabicName : p.name}
        </h3>
        <p className="text-xs text-slate-500 dark:text-gray-400 line-clamp-3 mb-4 leading-relaxed font-light">
          {language === 'ar' ? p.arabicDescription : p.description}
        </p>

        <div className="flex gap-4 mt-auto pt-3 border-t border-black/5 dark:border-white/5">
          {p.liveDemo && (
            <Link
              to={p.liveDemo}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-gray-400 hover:text-purple-500 flex items-center gap-1 transition-colors"
            >
              <Icon icon={isGraphicDesign ? "lucide:palette" : "lucide:external-link"} /> {isGraphicDesign ? (language === 'ar' ? 'المعرض' : 'Gallery') : 'Demo'}
            </Link>
          )}
          {p.github && (
            <Link
              to={p.github}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-gray-400 hover:text-purple-500 flex items-center gap-1 transition-colors"
            >
              <Icon icon="mdi:github" /> Code
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  )
}

// 2. CORE HOME PAGE COMPONENT
const Home = ({ language, languageText, darkMode }) => {
  const [selectedProject, setSelectedProject] = useState(null)

  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = pdf; a.download = 'CV.pdf'
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const webPackages = servicesData.web.filter(s => s.features).slice(0, 3)
  const featuredProjects = projectsData.slice(0, 3)

  const container = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.3 } } }

  return (
    <motion.div exit={{ opacity: 0, transition: { duration: 0.2 } }} className="w-full overflow-hidden">

      {/* ═══ HERO ═══════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center" style={{ background: darkMode ? 'linear-gradient(135deg,#07070f 0%,#0b0b1a 60%,#0e0a1e 100%)' : 'linear-gradient(135deg,#f4f7ff 0%,#eaeeff 60%,#e2e8fc 100%)' }}>
        <FloatingOrb style={{ width: 500, height: 500, top: '-10%', left: '-10%', background: `radial-gradient(circle,${darkMode ? 'rgba(82,92,235,0.18)' : 'rgba(82,92,235,0.12)'},transparent 70%)`, delay: 0 }} />
        <FloatingOrb style={{ width: 400, height: 400, bottom: '-5%', right: '-5%', background: `radial-gradient(circle,${darkMode ? 'rgba(168,85,247,0.14)' : 'rgba(168,85,247,0.1)'},transparent 70%)`, delay: 2 }} />
        <FloatingOrb style={{ width: 300, height: 300, top: '30%', right: '20%', background: `radial-gradient(circle,${darkMode ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.06)'},transparent 70%)`, delay: 4 }} />

        {/* Dot grid */}
        <div className={`absolute inset-0 ${darkMode ? 'opacity-[0.04]' : 'opacity-[0.07]'}`} style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='20' cy='20' r='0.8' fill='${darkMode ? '%23ffffff' : '%23000000'}'/%3E%3C/svg%3E")` }} />

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-10 lg:px-20 w-full py-24 md:py-0">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

            {/* Left content */}
            <motion.div variants={container} initial="hidden" animate="visible" className="flex-1 text-center lg:text-start">

              {/* Studio Eyebrow Badge */}
              {/* <motion.div variants={fadeUp(0.05)} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6"
                style={{
                  background: darkMode ? 'rgba(82,92,235,0.12)' : 'rgba(82,92,235,0.08)',
                  border: darkMode ? '1px solid rgba(82,92,235,0.3)' : '1px solid rgba(82,92,235,0.2)',
                }}>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-darktheme dark:text-white">
                  {language === 'en' ? 'TORKY STUDIO' : 'استوديو تركي'}
                </span>
                <span className="text-[10px] text-gray-400 font-bold">•</span>
                <span className="text-[11px] font-bold" style={{ color: '#525ceb' }}>
                  {language === 'en' ? 'Digital Lab & Portfolio' : 'معمل رقمي ومعرض أعمال'}
                </span>
              </motion.div> */}

              <motion.h1 variants={fadeUp(0.1)} className="text-5xl md:text-7xl lg:text-8xl font-black text-darktheme dark:text-white leading-none tracking-tighter mb-4">
                {language === 'en' ? <>Mohamed<br /><span className="text-gradient">Torky</span></> : languageText.MyName}
              </motion.h1>

              <motion.div variants={fadeUp(0.2)} className="flex items-center justify-center lg:justify-start gap-2.5 flex-wrap mb-4">
                <span className="text-lg md:text-xl font-black" style={{ color: '#525ceb' }}>
                  {language === 'en' ? 'Full-Stack Engineer & Data Scientist' : (languageText.SoftwareEngineer || 'مهندس برمجيات متكامل وعالم بيانات')}
                </span>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="text-sm md:text-base font-bold text-slate-600 dark:text-gray-300 px-2.5 py-0.5 rounded-lg bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10">
                  {language === 'en' ? 'Founder of Torky Studio' : 'مؤسس استوديو تركي'}
                </span>
              </motion.div>

              <motion.p variants={fadeUp(0.3)} className="text-base text-slate-600 dark:text-gray-400 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
                {language === 'en'
                  ? "Welcome to Torky Studio — the creative engineering practice and portfolio of Mohamed Torky. I specialize in designing and developing high-converting websites, web applications, and bespoke celebratory platforms for businesses and couples."
                  : "مرحباً بكم في استوديو تركي — المساحة الهندسية والإبداعية لمحمد تركي. متخصص في تصميم وتطوير مواقع وتطبيقات ويب استثنائية وعالية التحويل، ومنصات رقمية مخصصة للعلامات التجارية والمناسبات الخاصة."}
              </motion.p>

              <motion.div variants={fadeUp(0.4)} className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
                <Link to="/services"
                  className="flex items-center gap-2 px-6 py-3.5 rounded-2xl font-black text-sm text-white uppercase tracking-wide hover:scale-105 active:scale-95 transition-transform"
                  style={{ background: 'linear-gradient(135deg,#525ceb,#a855f7)', boxShadow: '0 12px 40px rgba(82,92,235,0.35)' }}>
                  <Icon icon="lucide:briefcase" /> {language === 'en' ? 'Hire Me' : 'وظفني'}
                </Link>
                <button onClick={handleDownload}
                  className="flex items-center gap-2 px-6 py-3.5 rounded-2xl font-bold text-sm uppercase tracking-wide hover:scale-105 active:scale-95 transition-transform duration-200 text-darktheme dark:text-white"
                  style={{ background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)' }}>
                  <Icon icon="lucide:download" /> {languageText.DownloadCV}
                </button>
              </motion.div>

              <motion.div variants={fadeUp(0.5)} className="flex gap-4 justify-center lg:justify-start">
                {[
                  { href: 'mailto:mohamed2003torky@gmail.com', icon: 'mdi:gmail', color: '#ea4335' },
                  { href: 'https://github.com/MdTorky', icon: 'mdi:github', color: '#000000' },
                  { href: 'https://www.linkedin.com/in/mdtorky/', icon: 'mdi:linkedin', color: '#0a66c2' },
                  { href: 'http://wa.me/201554206775', icon: 'ic:baseline-whatsapp', color: '#25d366' },
                ].map(({ href, icon, color }) => (
                  <Link key={href} to={href} target="_blank"
                    className="w-10 h-10 flex items-center justify-center rounded-xl text-lg transition-transform duration-200 hover:scale-110"
                    style={{
                      background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                      color: darkMode ? 'rgba(200,200,220,0.7)' : 'rgba(71,85,105,0.8)'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = color; e.currentTarget.style.borderColor = color + '50'; }}
                    onMouseLeave={e => {
                      e.currentTarget.style.color = darkMode ? 'rgba(200,200,220,0.7)' : 'rgba(71,85,105,0.8)';
                      e.currentTarget.style.borderColor = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
                    }}>
                    <Icon icon={icon} />
                  </Link>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — photo + orbiting ring */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex-shrink-0 flex items-center justify-center">
              <div className="relative w-64 h-64 md:w-80 md:h-80 lg:w-[420px] lg:h-[420px]">
                {/* Gradient ring */}
                <div className="absolute inset-0 rounded-full p-1" style={{ background: 'linear-gradient(135deg,#525ceb,#a855f7,#10b981,#525ceb)', animation: 'spin 8s linear infinite' }}>
                  <div className="w-full h-full rounded-full" style={{ background: darkMode ? '#0b0b1a' : '#f4f7ff' }} />
                </div>
                <img src={Me} alt="Mohamed Torky" className="absolute inset-2 w-[calc(100%-16px)] h-[calc(100%-16px)] object-cover object-top rounded-full" style={{ mixBlendMode: 'luminosity' }} />

                {/* Floating stat chips */}
                <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-4 -right-4 px-3 py-2 rounded-xl text-xs font-black text-white"
                  style={{ background: 'linear-gradient(135deg,#525ceb,#7c3aed)', boxShadow: '0 8px 24px rgba(82,92,235,0.4)' }}>
                  {language === 'en' ? "🏆 Hackathon Winner" : "🏆 فائز في الهكاثون"}
                </motion.div>
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
                  className="absolute -bottom-4 -left-4 px-3 py-2 rounded-xl text-xs font-black text-white"
                  style={{ background: 'linear-gradient(135deg,#10b981,#06b6d4)', boxShadow: '0 8px 24px rgba(16,185,129,0.4)' }}>
                  {language === 'en' ? "✦ GPA 3.82/4.0" : "✦ معدل تراكمي 3.82"}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-gray-500 dark:text-gray-600">
          <span className="text-[10px] uppercase tracking-widest font-bold">{language === 'en' ? 'Scroll' : 'مرر للأسفل'}</span>
          <Icon icon="lucide:chevron-down" className="text-sm" />
        </motion.div>
      </section>

      {/* ═══ TECH TICKER ════════════════════════════════════════════ */}
      <div className="py-5 overflow-hidden" style={{ background: 'rgba(82,92,235,0.06)', borderTop: '1px solid rgba(82,92,235,0.15)', borderBottom: '1px solid rgba(82,92,235,0.15)' }}>
        <motion.div animate={{ x: ['0%', '-50%'] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="flex gap-10 whitespace-nowrap">
          {[...TECH, ...TECH].map((t, i) => (
            <span key={i} className="text-xs font-black uppercase tracking-widest text-slate-500 dark:text-gray-400 flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-bluetheme" />{t}
            </span>
          ))}
        </motion.div>
      </div>

      {/* ═══ STATS ══════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20" style={{ background: darkMode ? '#08080f' : '#f8fafc' }}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: '3.82', label: language === 'en' ? "Master's GPA" : "تقدير الماجستير", icon: 'lucide:brain', color: '#a855f7' },
              { num: '10+', label: language === 'en' ? 'Projects Built' : "المشاريع المكتملة", icon: 'lucide:folder-code', color: '#525ceb' },
              { num: '1st', label: language === 'en' ? 'Microsoft Hackathon' : "المركز الأول في مسابقة مايكروسوفت", icon: 'lucide:trophy', color: '#f59e0b' },
              { num: '4.9★', label: language === 'en' ? 'Client Rating' : "تقييم العملاء", icon: 'lucide:star', color: '#10b981' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                className="flex flex-col items-center text-center p-6 rounded-2xl"
                style={{
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
                  boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.02)'
                }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-lg"
                  style={{ background: s.color + '15', color: s.color }}>
                  <Icon icon={s.icon} />
                </div>
                <div className="text-3xl font-black mb-1" style={{ color: s.color }}>{s.num}</div>
                <div className="text-xs text-slate-500 dark:text-gray-500 font-bold uppercase tracking-widest">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TORKY STUDIO SPOTLIGHT ════════════════════════════════ */}
      <section className="py-10 md:py-14 relative overflow-hidden" style={{ background: darkMode ? '#080812' : '#ffffff' }}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl p-8 md:p-12 overflow-hidden"
            style={{
              background: darkMode
                ? 'linear-gradient(135deg, rgba(0,210,255,0.08) 0%, rgba(2,132,199,0.05) 45%, rgba(5,8,17,0.9) 100%)'
                : 'linear-gradient(135deg, rgba(0,210,255,0.06) 0%, rgba(2,132,199,0.04) 100%)',
              border: darkMode ? '1px solid rgba(0,210,255,0.25)' : '1px solid rgba(0,210,255,0.2)',
              boxShadow: darkMode ? '0 20px 60px rgba(0,210,255,0.08)' : '0 10px 30px rgba(0,210,255,0.05)'
            }}
          >
            {/* Glow orb */}
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #00d2ff, #0284c7)' }} />

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12">
              
              {/* TS Emblem Showcase */}
              <div className="relative flex-shrink-0 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full blur-2xl opacity-60 pointer-events-none"
                  style={{ background: 'radial-gradient(circle, rgba(0,210,255,0.5), rgba(2,132,199,0.2), transparent 70%)' }} />
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                  className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-44 md:h-44 rounded-full p-1.5 shadow-2xl cursor-pointer"
                  style={{
                    background: 'linear-gradient(135deg, #00d2ff, #0284c7, #070b16, #00d2ff)',
                    boxShadow: '0 0 35px rgba(0,210,255,0.35)'
                  }}
                >
                  <img
                    src="/TS.png"
                    alt="Torky Studio Emblem"
                    className="w-full h-full object-cover rounded-full bg-[#050811]"
                  />
                </motion.div>
              </div>

              {/* Text Info */}
              <div className="flex-1 text-start">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider mb-4"
                  style={{ background: 'rgba(0,210,255,0.12)', color: '#00d2ff', border: '1px solid rgba(0,210,255,0.3)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00d2ff] animate-pulse" />
                  {language === 'en' ? 'The Studio & Practice' : 'الاستوديو الهندسي الإبداعي'}
                </div>
                <h3 className="text-2xl md:text-4xl font-black text-darktheme dark:text-white tracking-tight mb-3">
                  <span className="text-[#00d2ff]">Torky Studio</span>
                  <span className="text-base md:text-lg font-semibold text-slate-500 dark:text-gray-400 block sm:inline sm:ml-3">
                    {language === 'en' ? '— Founded & Directed by Mohamed Torky' : '— تأسيس وهندسة: محمد تركي'}
                  </span>
                </h3>
                <p className="text-sm md:text-base text-slate-600 dark:text-gray-300 leading-relaxed font-normal">
                  {language === 'en'
                    ? "If you arrived here through a client website credit (« Created and designed by Torky Studio »), welcome! Torky Studio is the independent development practice where technical engineering meets boutique visual design. From high-conversion corporate web platforms to bespoke digital wedding invitations, every project is crafted from scratch."
                    : "إذا وصلت إلى هنا عبر رابط في أسفل موقع أحد العملاء (« تم التصميم والتطوير بواسطة استوديو تركي »)، فأهلاً بك! استوديو تركي هو معملي المستقل الذي يجمع بين الهندسة البرمجية والتصميم البصري الفاخر. من مواقع الشركات عالية التحويل إلى دعوات الزفاف والمناسبات الرقمية المخصصة، كل عمل يُبنى بإتقان وشغف."}
                </p>

                {/* Studio capabilities tags */}
                <div className="flex flex-wrap gap-2 mt-5">
                  {[
                    { label: language === 'en' ? 'High-Performance Web' : 'مواقع ويب عالية الأداء', icon: 'lucide:code' },
                    { label: language === 'en' ? 'Wedding & Event Portals' : 'بوابات مناسبات وأفراح رقمية', icon: 'lucide:heart' },
                    { label: language === 'en' ? 'UI/UX & Interactive Design' : 'تصميم واجهات وتجارب تفاعلية', icon: 'lucide:palette' },
                    { label: language === 'en' ? 'Full-Stack & Data Intelligence' : 'برمجة متكاملة وذكاء اصطناعي', icon: 'lucide:cpu' },
                  ].map((tag, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold text-slate-700 dark:text-cyan-100 bg-white/70 dark:bg-[#0b1324]/80 border border-black/5 dark:border-[#00d2ff]/20">
                      <Icon icon={tag.icon} className="text-[#00d2ff]" />
                      {tag.label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Card / Buttons */}
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-auto flex-shrink-0">
                <Link to="/services"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-black text-sm uppercase tracking-wide hover:scale-105 transition-transform"
                  style={{
                    background: 'linear-gradient(135deg, #00d2ff 0%, #0284c7 100%)',
                    boxShadow: '0 10px 30px rgba(0,210,255,0.4)',
                    color: '#050811'
                  }}>
                  <Icon icon="lucide:arrow-up-right" className="text-base" />
                  {language === 'en' ? 'Start a Project' : 'ابدأ مشروعك معنا'}
                </Link>
                <Link to="/projects"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-bold text-sm text-darktheme dark:text-white uppercase tracking-wide hover:bg-[#00d2ff]/10 transition-colors"
                  style={{ border: darkMode ? '1px solid rgba(0,210,255,0.3)' : '1px solid rgba(0,0,0,0.1)' }}>
                  <Icon icon="lucide:folder-code" className="text-[#00d2ff]" />
                  {language === 'en' ? 'Studio Showcase' : 'معرض أعمال الاستوديو'}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ SERVICES PREVIEW ═══════════════════════════════════════ */}
      <section className="py-16 md:py-24" style={{ background: darkMode ? 'linear-gradient(180deg,#08080f,#0a0a15)' : 'linear-gradient(180deg,#f8fafc,#f1f5f9)' }}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 lg:px-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-end justify-between mb-10 flex-wrap gap-4 text-start">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: '#525ceb', fontFamily: 'monospace' }}>{'// services'}</p>
              <h2 className="text-3xl md:text-4xl font-black text-darktheme dark:text-white">{language === 'en' ? 'What I Build' : 'ما أبنيه'}</h2>
            </div>
            <Link to="/services" className="flex items-center gap-2 text-sm font-black uppercase tracking-wide transition-colors duration-200 hover:text-darktheme dark:hover:text-white"
              style={{ color: '#525ceb' }}>
              {language === 'en' ? 'All Services' : 'جميع الخدمات'} <Icon icon="lucide:arrow-right" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {webPackages.map((pkg, i) => {
              const colors = { 'from-purple-600 to-indigo-600': '#7c3aed', 'from-blue-600 to-cyan-600': '#2563eb', 'from-teal-500 to-emerald-500': '#10b981', 'from-orange-500 to-yellow-500': '#f97316' }
              const c = colors[pkg.color] || '#525ceb'
              return (
                <motion.div key={pkg.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                  whileHover={{ y: -6 }}
                  className="relative p-6 rounded-3xl overflow-hidden group"
                  style={{
                    background: darkMode ? '#0d0d1a' : '#ffffff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
                    boxShadow: darkMode ? 'none' : '0 10px 30px rgba(0,0,0,0.02)'
                  }}>
                  <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${pkg.color}`} />
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl text-white mb-4"
                    style={{ background: `linear-gradient(135deg,${c},${c}aa)`, boxShadow: `0 6px 20px ${c}30` }}>
                    <Icon icon={pkg.icon} />
                  </div>
                  <h3 className="text-base font-black text-darktheme dark:text-white uppercase tracking-tight mb-2">{language === 'en' ? pkg.name : pkg.arabicName}</h3>
                  <p className="text-xs text-slate-500 dark:text-gray-500 mb-5 leading-relaxed line-clamp-2">{language === 'en' ? pkg.description : pkg.arabicDescription}</p>
                  <div className="flex items-center justify-between">
                    {SHOW_PRICES && <span className="text-xl font-black" style={{ color: c }}>{pkg.price}</span>}
                    <Link to={`/services/${pkg.id}`} className="flex items-center gap-1 text-xs font-black uppercase tracking-wide transition-[gap,color] duration-200 group-hover:gap-2"
                      style={{ color: c }}>
                      {language === 'en' ? 'Details' : 'تفاصيل'} <Icon icon="lucide:arrow-right" className="text-sm" />
                    </Link>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ═══ FEATURED PROJECTS ══════════════════════════════════════ */}
      <section className="py-16 md:py-24" style={{ background: darkMode ? '#0a0a15' : '#f1f5f9' }}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 lg:px-20">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-end justify-between mb-10 flex-wrap gap-4 text-start">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: '#a855f7', fontFamily: 'monospace' }}>{'// projects'}</p>
              <h2 className="text-3xl md:text-4xl font-black text-darktheme dark:text-white">{language === 'en' ? 'Featured Work' : 'أبرز أعمالي'}</h2>
            </div>
            <Link to="/projects" className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-purple-600 dark:text-purple-400 hover:text-darktheme dark:hover:text-white">
              {language === 'en' ? 'All Projects' : 'جميع المشاريع'} <Icon icon="lucide:arrow-right" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredProjects.map((p, i) => (
              <HomeProjectCard
                key={p.id}
                p={p}
                i={i}
                language={language}
                darkMode={darkMode}
                onClick={() => setSelectedProject(p)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-24" style={{ background: darkMode ? '#08080f' : '#f8fafc' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 blur-3xl opacity-30 rounded-full" style={{ background: 'radial-gradient(circle,#525ceb,#a855f7)' }} />
              <h2 className="relative text-4xl md:text-6xl font-black text-darktheme dark:text-white leading-tight">
                {language === 'en' ? <>Let's build something <span className="text-gradient">incredible</span></> : <>لنقم ببناء شيء <span className="text-gradient">مذهل</span></>}
              </h2>
            </div>
            <p className="text-slate-600 dark:text-gray-400 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
              {language === 'en' ? "Have a project in mind? Let's turn your vision into reality." : "هل لديك مشروع في ذهنك؟ لنحول رؤيتك إلى واقع ملموس."}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/services" className="flex items-center gap-2 px-8 py-4 rounded-2xl font-black text-sm text-white uppercase tracking-wide hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg,#525ceb,#a855f7)', boxShadow: '0 12px 40px rgba(82,92,235,0.35)' }}>
                <Icon icon="lucide:briefcase" /> {language === 'en' ? 'View Services' : 'الخدمات'}
              </Link>
              <Link to="/about" className="flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-sm uppercase tracking-wide hover:scale-105 transition-transform duration-200 text-darktheme dark:text-white"
                style={{ background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)' }}>
                <Icon icon="lucide:user" /> {language === 'en' ? 'About Me' : 'من أنا'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Dynamic Cinematic Detail Modal (Opens directly from Homepage clicks!) */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectDetailModal
            project={selectedProject}
            language={language}
            languageText={languageText}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>

    </motion.div>
  )
}

export default Home
